import { useState } from 'react';
import { Link } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import SEOHead from '@/components/feature/SEOHead';

type AuthMode = 'login' | 'register';

export default function AuthPage() {
  const [mode, setMode] = useState<AuthMode>('login');

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Noto Sans SC', sans-serif" }}>
      <SEOHead
        title="登录 / 注册 - 富缘佳旺 FortuneMatch"
        description="登录或注册富缘佳旺账号，开启真实认证的婚恋之旅。"
        noIndex={true}
      />
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex flex-col justify-between w-[520px] flex-shrink-0 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/ff0307c1ad13031e1f59c455bd460811.jpg"
          alt="FortuneMatch"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70"></div>

        {/* Top Logo */}
        <div className="relative z-10 p-8">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="富缘佳旺"
              className="w-12 h-12 rounded-full object-cover border-2 border-white/50"
            />
            <div>
              <div className="text-white text-xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif" }}>富缘佳旺</div>
              <div className="text-red-300 text-xs tracking-widest">FortuneMatch.com</div>
            </div>
          </Link>
        </div>

        {/* Center Content */}
        <div className="relative z-10 px-8 py-6">
          <h2 className="text-white text-3xl font-bold mb-4 leading-tight" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            真实认证<br />
            <span className="text-red-300">视频交友</span><br />
            教育为本
          </h2>
          <p className="text-white/70 text-sm leading-relaxed mb-8">
            每一位会员均经过严格的实名认证与视频核验，确保您遇见的每一个人都是真实的。
          </p>

          {/* Trust Badges */}
          <div className="flex flex-col gap-3">
            {[
              { icon: 'ri-shield-check-line', label: '实名认证', desc: '身份证 + 人脸核验' },
              { icon: 'ri-video-chat-line', label: '视频认证', desc: '1对1视频确认本人' },
              { icon: 'ri-graduation-cap-line', label: '学历优先', desc: '教育背景核心匹配' },
              { icon: 'ri-lock-password-line', label: '隐私保护', desc: '证件信息严格加密' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-red-300`}></i>
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">{item.label}</div>
                  <div className="text-white/50 text-xs">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="relative z-10 p-8 border-t border-white/10">
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { num: '120万+', label: '认证会员' },
              { num: '98%', label: '真实率' },
              { num: '280万+', label: '成功牵手' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-white text-lg font-bold">{s.num}</div>
                <div className="text-white/50 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-4 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="富缘佳旺"
              className="w-8 h-8 rounded-full object-cover"
            />
            <span className="text-red-700 font-bold text-base" style={{ fontFamily: "'Noto Serif SC', serif" }}>富缘佳旺</span>
          </Link>
          <Link to="/" className="text-gray-400 text-sm cursor-pointer hover:text-gray-600">
            <i className="ri-home-line mr-1"></i>返回首页
          </Link>
        </div>

        {/* Form Area */}
        <div className="flex-1 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="w-full max-w-md">
            {/* Desktop Back Link */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <Link to="/" className="flex items-center gap-1.5 text-gray-400 text-sm hover:text-gray-600 cursor-pointer transition-colors">
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
              <div className="text-xs text-gray-400">
                {mode === 'login' ? '还没有账号？' : '已有账号？'}
                <button
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="text-red-500 font-medium ml-1 cursor-pointer hover:underline"
                >
                  {mode === 'login' ? '免费注册' : '立即登录'}
                </button>
              </div>
            </div>

            {/* Title */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                {mode === 'login' ? '欢迎回来' : '加入富缘佳旺'}
              </h1>
              <p className="text-gray-400 text-sm">
                {mode === 'login'
                  ? '登录您的账号，继续您的缘分之旅'
                  : '三步完成注册，开启真实认证的婚恋之旅'}
              </p>
            </div>

            {/* Mode Toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
              {(['login', 'register'] as AuthMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === m ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {m === 'login' ? <><i className="ri-login-box-line mr-1"></i>会员登录</> : <><i className="ri-user-add-line mr-1"></i>免费注册</>}
                </button>
              ))}
            </div>

            {/* Form */}
            {mode === 'login'
              ? <LoginForm onSwitch={() => setMode('register')} />
              : <RegisterForm onSwitch={() => setMode('login')} />
            }
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © 2024 富缘佳旺 FortuneMatch.com · 
            <a href="#" className="hover:text-gray-600 cursor-pointer mx-1">隐私政策</a>·
            <a href="#" className="hover:text-gray-600 cursor-pointer mx-1">用户协议</a>·
            <a href="#" className="hover:text-gray-600 cursor-pointer mx-1">联系我们</a>
          </p>
        </div>
      </div>
    </div>
  );
}
